/**
 * UI-6c Task 9 — CsatResultsPage.
 *
 * Tenant-admin CSAT analytics page. Composition (spec §4.2):
 *
 *   - Wrapped in `<RequirePermission permission="reports:view">`.
 *   - Filter row: a date-range `Calendar` + a `Dropdown` for group-by.
 *   - 3 stat tiles for `summary.overall` (avg score / response count / FCR
 *     rate).
 *   - PrimeReact `Chart` (bar) shows `summary.buckets` — x-axis = bucket
 *     key, y-axis = avg score, plus a second dataset for response counts.
 *   - PrimeReact `DataTable` shows the same buckets in tabular form.
 *   - Empty state: "No CSAT responses for this filter." when zero buckets.
 *
 * Per plan §0.A, group-by is `'agent' | 'queue' | 'day'` only — channel is
 * dropped for Phase 1 (csat_surveys has no channel column; see also the
 * spec's "Future plans" section). The plan's reference snippet (Task 9)
 * still listed "By channel" but §0.A is authoritative.
 *
 * The `dateRange` filter is sent verbatim — the page does NOT enforce a
 * complete range. The hook serialises a single-ended range happily (only
 * one of `from`/`to` set), so partially picked dates still narrow the
 * server query.
 */
import { useMemo, useState, type JSX } from 'react'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { Chart } from 'primereact/chart'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'

import PageHeader from '../../components/admin/PageHeader.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import {
  useCsatSummary,
  type CsatSummaryBucket,
} from '../../hooks/useCsatSummary.js'

// ---------------------------------------------------------------------------
// Types + constants
// ---------------------------------------------------------------------------

type GroupBy = 'agent' | 'queue' | 'day'

const GROUP_BY_OPTIONS: ReadonlyArray<{ label: string; value: GroupBy }> = [
  { label: 'By day', value: 'day' },
  { label: 'By agent', value: 'agent' },
  { label: 'By queue', value: 'queue' },
] as const

const GROUP_BY_LABEL: Record<GroupBy, string> = {
  agent: 'agent',
  queue: 'queue',
  day: 'day',
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface ChartDataset {
  label: string
  data: number[]
  backgroundColor?: string
}

interface ChartData {
  labels: string[]
  datasets: ChartDataset[]
}

/**
 * Build a PrimeReact Chart payload from CSAT buckets. We surface two
 * datasets so the breakdown is meaningful at a glance: the bar height is
 * avg score (0 to the tenant's configured scale), and an annotation
 * dataset carries response counts.
 * Colors are deliberately neutral — the page header carries the heading;
 * the chart is a quick scan.
 */
function buildChartData(buckets: CsatSummaryBucket[]): ChartData {
  return {
    labels: buckets.map((b) => b.keyLabel),
    datasets: [
      {
        label: 'Avg score',
        data: buckets.map((b) => b.avgScore),
        backgroundColor: 'rgba(59, 130, 246, 0.7)',
      },
      {
        label: 'Responses',
        data: buckets.map((b) => b.responseCount),
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
      },
    ],
  }
}

const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: 'top' as const } },
}

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(0)}%`
}

// ---------------------------------------------------------------------------
// Stat tile (inline — no shared component exists)
// ---------------------------------------------------------------------------

interface StatProps {
  label: string
  value: string
  testId: string
}

function Stat({ label, value, testId }: StatProps): JSX.Element {
  return (
    <div
      data-testid={testId}
      className="rounded border border-gray-200 bg-white p-4 shadow-sm"
    >
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function CsatResultsPage(): JSX.Element {
  const [groupBy, setGroupBy] = useState<GroupBy>('day')
  // PrimeReact's range Calendar emits `[Date, null]` after the user's first
  // click, then `[Date, Date]` on the second. We mirror that nullable
  // structure here. A `null` outer value means "no filter".
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null])

  // Build the hook params conditionally so we don't pass `undefined` for
  // `from` / `to` under `exactOptionalPropertyTypes` — TS treats explicit
  // `undefined` as a different shape from "key absent".
  const csatParams: { by: GroupBy; from?: Date; to?: Date } = { by: groupBy }
  if (dateRange[0]) csatParams.from = dateRange[0]
  if (dateRange[1]) csatParams.to = dateRange[1]
  const { summary, loading, error } = useCsatSummary(csatParams)

  const chartData = useMemo(
    () => (summary ? buildChartData(summary.buckets) : null),
    [summary],
  )

  return (
    <RequirePermission permission="reports:view">
      <div className="space-y-4 p-4">
        <PageHeader
          title="CSAT Results"
          description="Aggregate CSAT scores across your selected window, grouped by day, agent, or queue."
        />

        {/* Filter row -------------------------------------------------- */}
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">Date range</span>
            <Calendar
              aria-label="Filter by submission date range"
              value={dateRange}
              onChange={(e) =>
                setDateRange(
                  (e.value as [Date | null, Date | null] | null) ?? [null, null],
                )
              }
              selectionMode="range"
              showIcon
              readOnlyInput
              placeholder="Any date"
            />
          </label>

          <label className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">Group by</span>
            <Dropdown
              aria-label="Group results by"
              value={groupBy}
              options={[...GROUP_BY_OPTIONS]}
              onChange={(e) => setGroupBy(e.value as GroupBy)}
            />
          </label>
        </div>

        {/* Error banner ------------------------------------------------ */}
        {error !== null && (
          <div
            role="alert"
            data-testid="csat-error"
            className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        {/* Body -------------------------------------------------------- */}
        {summary === null && loading ? (
          <div
            data-testid="csat-loading"
            className="p-4 text-center text-sm text-gray-500"
          >
            Loading…
          </div>
        ) : summary === null ? (
          // No data and not loading — defensive; shouldn't normally happen
          // for `reports:view` users with `by` always set, but render an
          // empty hint rather than nothing.
          <div className="p-4 text-center text-sm text-gray-500">
            No CSAT data available.
          </div>
        ) : summary.buckets.length === 0 ? (
          <div
            data-testid="csat-empty"
            className="p-4 text-center text-sm text-gray-500"
          >
            No CSAT responses for this filter.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Stat
                label="Avg score"
                value={`${summary.overall.avgScore.toFixed(1)}/${summary.scale}`}
                testId="stat-avg-score"
              />
              <Stat
                label="Responses"
                value={String(summary.overall.responseCount)}
                testId="stat-responses"
              />
              <Stat
                label="FCR rate"
                value={formatPercent(summary.overall.firstCallResolutionRate)}
                testId="stat-fcr-rate"
              />
            </div>

            <div className="h-72 rounded border border-gray-200 bg-white p-3">
              {chartData && (
                <Chart type="bar" data={chartData} options={CHART_OPTIONS} />
              )}
            </div>

            <DataTable
              value={summary.buckets}
              dataKey="key"
              className="rounded border border-gray-200 bg-white"
            >
              <Column field="keyLabel" header={`Group (${GROUP_BY_LABEL[groupBy]})`} />
              <Column
                field="avgScore"
                header="Avg score"
                body={(r: CsatSummaryBucket) => `${r.avgScore.toFixed(2)}/${summary.scale}`}
              />
              <Column field="responseCount" header="Responses" />
              <Column
                field="firstCallResolutionRate"
                header="FCR rate"
                body={(r: CsatSummaryBucket) =>
                  formatPercent(r.firstCallResolutionRate)
                }
              />
            </DataTable>
          </>
        )}
      </div>
    </RequirePermission>
  )
}
